import { Box } from '@repo/ui/Box';
import { clsx } from 'clsx';
import s from './ExchangeDetails.module.css';
import type { OrderItem } from '../../graphql/query/getOrder';
import type { ProductInfo } from '../../graphql/query/getResolutionOptions';
import { Flex } from '@repo/ui/Flex';
import { ExchangeAttributeChipGroup } from './ExchangeAttributeChipGroup';
import { useFormContext } from 'react-hook-form';
import { getMatchingVariant } from './helpers';
import { useItems } from '../../hooks/useItems';
import type { AttributeFormSchemaType } from './ExchangeLayout';
import { useEffect } from 'react';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { useWorkflow } from '../../workflow/hooks';
import { getValidImageUrl } from '../../utils/image';

export type ExchangeDetailsProps = {
  item: OrderItem;
  productInfo: ProductInfo;
};

export const ExchangeDetails = (props: ExchangeDetailsProps) => {
  const { item, productInfo } = props;
  const { attributeOptions, variants } = productInfo;
  const { order } = useWorkflow();

  const applicableAttributes = attributeOptions.filter((attr) =>
    variants.every((variant) =>
      variant.attributes.some((variantAttr) => variantAttr.name === attr.name),
    ),
  );
  const { watch, setValue, resetField, trigger } =
    useFormContext<AttributeFormSchemaType>();
  const isMobile = useIsMobile();

  const items = useItems();
  const originalVariantId = items.find((i) => i.id === item.id)?.variant?.id;
  const originalVariant = variants.find(
    (v) => v.variantId === originalVariantId,
  );
  const selectedAttributes = Object.entries(watch('attributes')).map(
    ([name, value]) => ({ name, value }),
  );
  const selectedVariant = getMatchingVariant({
    attributes: selectedAttributes,
    variants,
  });

  const displayedItem = selectedVariant ?? originalVariant ?? variants[0];

  // Get parent item's image as fallback
  const parentItem = order?.items?.find(
    (orderItem) => orderItem.id === item.id,
  );
  const parentItemImage = parentItem?.image?.url;

  useEffect(() => {
    if (!selectedVariant?.productId || !selectedVariant.variantId) {
      resetField('exchangeItem');
    } else {
      setValue('exchangeItem', {
        variantId: selectedVariant.variantId,
        productId: selectedVariant.productId,
      });
    }
    trigger('exchangeItem');
  }, [setValue, resetField, trigger, selectedVariant]);

  if (!displayedItem) return null;

  return (
    <Box className={clsx({ [s.grid]: true, [s.mobile]: isMobile })}>
      <Box className={s.imageSidebar} padding={['px-16', 'md:px-0']}>
        <img
          alt={displayedItem.name ?? ''}
          src={getValidImageUrl(displayedItem.image, parentItemImage)}
          className={s.image}
        />
      </Box>
      <Flex className={s.details} gap="g-32" direction="column">
        <Flex gap="g-8" direction="column" padding={['px-24', 'md:px-0']}>
          <h2 className={s.itemName}>{displayedItem.name}</h2>
          <p className={s.itemDescription}>
            {displayedItem.price?.formattedString}
          </p>
        </Flex>

        <Flex gap="g-32" direction="column" padding={['px-16', 'md:px-0']}>
          {applicableAttributes.map((attr) => (
            <ExchangeAttributeChipGroup
              key={attr.name}
              name={attr.name}
              options={attr.options}
              itemId={item.id}
              variants={variants}
            />
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};
